/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useContext } from 'react';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { PeekMenuIdContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { convertToConfigurableMenuId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/convert-to-configurable-menu-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { SECTION_ITEM_MAP } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { isGlobalAppMenuId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';
import { GLOBAL_APP_MENU_ID_TO_APS_KEY } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getUserNavigationItemLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';

export type MoreMenuItemProps = {
	// eslint-disable-next-line jira/react/handler-naming
	closeMenu: () => void;
	menuId: string;
	key: string;
	getAppShortcutURL?: (apsKey: string) => string | undefined;
};

export function MoreMenuItem({
	menuId,
	closeMenu,
	key,
	getAppShortcutURL,
}: MoreMenuItemProps): React.JSX.Element {
	const { setPeekMenuId } = useContext(PeekMenuIdContext);

	const configurableId = convertToConfigurableMenuId(menuId);
	const isGlobalApp = isGlobalAppMenuId(menuId);

	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(configurableId);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const getProductUrl = useCallback(() => {
		if (!isGlobalApp || !getAppShortcutURL) {
			return;
		}

		if (GLOBAL_APP_MENU_ID_TO_APS_KEY[menuId]) {
			const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[menuId];
			return getAppShortcutURL(apsKey);
		}
	}, [isGlobalApp, getAppShortcutURL, menuId]);

	// This is part of an intermediate state workaround where links are accounted for;
	// we don't expect links to be in the More menu at the end state,
	// at which point this and the MenuLinkItem below can be removed.

	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	const url = getProductUrl() || USER_NAVIGATION_ITEM_LINKS[menuId];

	const onClick = useCallback(() => {
		closeMenu();

		if (!isGlobalApp) {
			// Global apps open in a new tab onClick so they don't need to be a peek-item
			setPeekMenuId(configurableId);
		}

		sendSectionItemAnalyticsEvent({
			menuId: configurableId,
			sectionItem: SECTION_ITEM_MAP[configurableId],
		});
	}, [closeMenu, configurableId, isGlobalApp, sendSectionItemAnalyticsEvent, setPeekMenuId]);

	const menuItemContent = url ? (
		<LinkMenuItem
			elemBefore={icon}
			href={url}
			onClick={onClick}
			key={key}
			target={isGlobalApp ? '_blank' : undefined}
		>
			{formattedMessage}
		</LinkMenuItem>
	) : (
		<ButtonMenuItem elemBefore={icon} onClick={onClick} key={key}>
			{formattedMessage}
		</ButtonMenuItem>
	);

	if (configurableId === 'jira.sidebar.operations' && fg('ops-overview-disc-spotlights')) {
		return <div id="ops-disc-operations-menu-item-spotlight-target">{menuItemContent}</div>;
	}

	return <>{menuItemContent}</>;
}
